import { createApi } from "@reduxjs/toolkit/query/react";
import { ApiEndpoints, baseQueryWithAuth } from "../../../core";
import { ApiResponse, IMetric } from "../../../types";

type GetMetricsArg = void | {
  page?: number;
  limit?: number;
};

export const metricsApi = createApi({
  reducerPath: "metricsApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Metrics"],

  endpoints: (builder) => ({
    getMetrics: builder.query<IMetric[], GetMetricsArg>({
      query: (arg) => {
        let page: number | undefined;
        let limit: number | undefined;

        if (typeof arg === "object" && arg !== null) {
          page = arg.page;
          limit = arg.limit;
        }

        const params = new URLSearchParams();
        if (page !== undefined) params.append("page", String(page));
        if (limit !== undefined) {
          params.append("limit", String(limit));
        } else {
          // Backwards-compatible default to fetch all when no limit is provided
          params.append("limit", "-1");
        }

        return {
          url: `${ApiEndpoints.getAllMetric}?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (res: ApiResponse<IMetric[]>) => {
        return res?.result ?? res?.results ?? [];
      },
      providesTags: ["Metrics"],
    }),

    getMetricById: builder.query<IMetric, string>({
      query: (id) => ({
        url: `${ApiEndpoints.metric}/${id}`,
        method: "GET",
      }),
      transformResponse: (res: ApiResponse<IMetric>) => {
        return res?.result!;
      },
      providesTags: (_result, _error, id) => [{ type: "Metrics", id }],
    }),

    createMetric: builder.mutation<ApiResponse<IMetric>, Partial<IMetric>>({
      query: (data) => ({
        url: ApiEndpoints.createMetric,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Metrics"],
    }),

    editMetric: builder.mutation<
      ApiResponse<IMetric>,
      { id: string; data: Partial<IMetric> }
    >({
      query: ({ id, data }) => ({
        url: `${ApiEndpoints.editMetric}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "Metrics",
        { type: "Metrics", id },
      ],
    }),

    deleteMetric: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `${ApiEndpoints.deleteMetric}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Metrics"],
    }),
  }),
});

export const {
  useGetMetricsQuery,
  useGetMetricByIdQuery,
  useCreateMetricMutation,
  useEditMetricMutation,
  useDeleteMetricMutation,
} = metricsApi;
